import { assembleTeamMetricViews, resolveFetchWindow } from "@/analytics/data/assemble-explore";
import { fetchAnalyticsRawData } from "@/analytics/data/fetch-analytics-data";
import { listRepOptions } from "@/analytics/core/scope-entities";
import type { CompareWindow, ExploreMetricView, PeriodWindow } from "@/analytics/core/types";
import { buildTeamGroups } from "@/grid/data/assemble-grid";
import { getDepartment } from "@/grid/config/registry";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

export function useTeamDashboardData(
  teamGroupKey: string,
  period: PeriodWindow,
  compare: CompareWindow,
) {
  const department = getDepartment("sales");

  return useQuery({
    queryKey: ["team-dashboard", teamGroupKey, period, compare],
    queryFn: async (): Promise<{ teamName: string; metrics: ExploreMetricView[] }> => {
      if (!department) throw new Error("Sales department not configured");

      const fetchWindow = resolveFetchWindow(period, compare);
      const raw = await fetchAnalyticsRawData(department, fetchWindow);
      const groups = buildTeamGroups(raw.teams, department);
      const group = groups.find((row) => row.groupKey === teamGroupKey);

      if (!group) {
        throw new Error("Team not found");
      }

      const repOptions = listRepOptions(raw, department, [group.id]);
      const repIds = repOptions.map((rep) => rep.id);
      const metrics = assembleTeamMetricViews(department, raw, {
        teamGroupId: group.id,
        teamName: group.name,
        repIds,
        period,
        compare,
      });

      return { teamName: group.name, metrics };
    },
    enabled: Boolean(teamGroupKey && department),
    staleTime: 5 * 60_000,
    placeholderData: keepPreviousData,
  });
}
